import React, { useRef, useCallback, useEffect, useMemo } from 'react';
import Stack from '@mui/material/Stack';
import { useSelector, useDispatch } from 'react-redux';
import { Drawer } from '@base-ui/react/drawer';
import { FloatingResources } from './FloatingResources';
import { FloatingWelcomeCard } from './FloatingWelcomeCard';
import { FloatingCommunityJournal } from './FloatingCommunityJournal';
import { FloatingPatientStories } from './FloatingPatientStories';
import { FloatingSearchResults } from './FloatingSearchResults';
import { setBottomSheetState } from '../../features/utilitySlice';
import styles from './MobileMapOverlay.module.css';

// How much of the sheet is visible when collapsed (peek state)
const PEEK_SNAP = '140px';
const NAVBAR_HEIGHT = 80;
const TOP_GAP = 16;

interface MobileMapOverlayProps {
  authStatus: string;
  profile: any;
}

export const MobileMapOverlay: React.FC<MobileMapOverlayProps> = ({ authStatus, profile }) => {
  const dispatch = useDispatch();
  const bottomSheetState = useSelector((state: any) => state.utility.showBottomSheet);
  const publicPinClicked = useSelector((state: any) => state.dataProcessing.publicPinClicked);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevPinClicked = useRef(publicPinClicked);

  // Expanded snap must match the popup's actual CSS height exactly
  // so Base UI's offset calculations are correct.
  const [expandedSnap, setExpandedSnap] = React.useState<string>(
    `${window.innerHeight - NAVBAR_HEIGHT - TOP_GAP}px`,
  );
  useEffect(() => {
    const update = () => setExpandedSnap(`${window.innerHeight - NAVBAR_HEIGHT - TOP_GAP}px`);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const snapPoints = useMemo<Drawer.Root.SnapPoint[]>(
    () => [PEEK_SNAP, expandedSnap],
    [expandedSnap],
  );

  // Controlled snap point state
  const [snapPoint, setSnapPoint] = React.useState<Drawer.Root.SnapPoint | null>(PEEK_SNAP);

  // Never allow full dismissal — redirect dismiss to peek snap
  const handleOpenChange = useCallback((open: boolean, _details: any) => {
    if (!open) {
      setSnapPoint(PEEK_SNAP);
    }
  }, []);

  // Intercept snap point changes: redirect null (dismiss attempt) back to peek
  const handleSnapPointChange = useCallback((newSnap: Drawer.Root.SnapPoint | null, _details: any) => {
    if (newSnap === null) {
      setSnapPoint(PEEK_SNAP);
    } else {
      setSnapPoint(newSnap);
    }
  }, []);

  const isExpanded = snapPoint === expandedSnap;

  const toggleSheet = useCallback(() => {
    setSnapPoint(isExpanded ? PEEK_SNAP : expandedSnap);
  }, [isExpanded, expandedSnap]);

  const expandSheet = useCallback(() => {
    setSnapPoint(expandedSnap);
  }, [expandedSnap]);


  // Listen for external triggers to expand (search, marker clicks)
  useEffect(() => {
    if (bottomSheetState) {
      expandSheet();
      dispatch(setBottomSheetState(false));
    }
  }, [bottomSheetState, dispatch, expandSheet]);

  // When a profile is opened, expand sheet and scroll to top after content loads
  useEffect(() => {
    if (publicPinClicked && publicPinClicked !== prevPinClicked.current) {
      expandSheet();

      const container = scrollContainerRef.current;
      if (!container) {
        prevPinClicked.current = publicPinClicked;
        return;
      }

      const scrollToTop = () => container.scrollTo({ top: 0, behavior: 'smooth' });

      // Scroll after sheet expansion animation
      const t1 = setTimeout(scrollToTop, 350);

      // Watch for DOM changes (profile content loading replaces skeletons) and scroll again
      let debounceTimer: ReturnType<typeof setTimeout>;
      const observer = new MutationObserver(() => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(scrollToTop, 100);
      });
      observer.observe(container, { childList: true, subtree: true });

      // Stop observing after 5 seconds
      const stopTimer = setTimeout(() => observer.disconnect(), 5000);

      prevPinClicked.current = publicPinClicked;
      return () => {
        clearTimeout(t1);
        clearTimeout(debounceTimer);
        clearTimeout(stopTimer);
        observer.disconnect();
      };
    }
    prevPinClicked.current = publicPinClicked;
  }, [publicPinClicked, expandSheet]);

  // Scroll chain for swipe-ignored nested scrollables.
  // Base UI ignores touch gestures on [data-base-ui-swipe-ignore] elements,
  // so we manually chain: nested scrollable boundary → Content scroll → sheet snap.
  // Uses document-level listeners because Drawer.Content lives inside a Portal
  // whose DOM may not be committed when the component-level useEffect first runs.
  useEffect(() => {
    let lastTouchY = 0;
    let nestedScrollable: HTMLElement | null = null;
    let contentEl: HTMLElement | null = null;
    let inIgnoredArea = false;
    let accumulatedPastTop = 0;

    const onTouchStart = (e: TouchEvent) => {
      lastTouchY = e.touches[0].clientY;
      accumulatedPastTop = 0;
      inIgnoredArea = false;
      nestedScrollable = null;
      contentEl = scrollContainerRef.current;
      if (!contentEl) return;

      // Check if touch originates inside a scroll-chain element
      // (elements that opted into manual scroll chaining via data-scroll-chain).
      // This is separate from data-base-ui-swipe-ignore: swipe-ignore just tells
      // the drawer to ignore the area; data-scroll-chain tells OUR handler to
      // manage the boundary handoff.
      let node = e.target as HTMLElement | null;
      while (node && node !== contentEl) {
        if (node.hasAttribute && node.hasAttribute('data-scroll-chain')) {
          inIgnoredArea = true;
          break;
        }
        node = node.parentElement;
      }
      if (!inIgnoredArea) return;

      // Find nearest scrollable between touch target and Drawer.Content
      node = e.target as HTMLElement | null;
      while (node && node !== contentEl) {
        if (node.nodeType === 1) { // Element node
          const style = window.getComputedStyle(node);
          if (
            (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
            node.scrollHeight > node.clientHeight
          ) {
            nestedScrollable = node;
            break;
          }
        }
        node = node.parentElement;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!inIgnoredArea || !contentEl) return;

      const currentY = e.touches[0].clientY;
      const frameDelta = currentY - lastTouchY; // positive = finger down
      lastTouchY = currentY;

      if (nestedScrollable) {
        const { scrollTop, scrollHeight, clientHeight } = nestedScrollable;
        const atTop = scrollTop <= 1;
        const atBottom = scrollTop >= scrollHeight - clientHeight - 1;

        // Nested scrollable can still scroll in this direction → do nothing
        if (frameDelta > 0 && !atTop) return;   // pulling down, not at top
        if (frameDelta < 0 && !atBottom) return; // pulling up, not at bottom
        if (frameDelta === 0) return;
      }

      // Chain to Drawer.Content scroll
      if (frameDelta > 0) {
        // Pulling down → scroll Content up
        if (contentEl.scrollTop > 0) {
          contentEl.scrollTop = Math.max(0, contentEl.scrollTop - frameDelta);
          accumulatedPastTop = 0;
        } else {
          // Content also at top → accumulate for sheet snap
          accumulatedPastTop += frameDelta;
          if (accumulatedPastTop > 40) {
            setSnapPoint(PEEK_SNAP);
            inIgnoredArea = false; // stop further handling
          }
        }
      } else if (frameDelta < 0) {
        // Pulling up → scroll Content down
        const maxScroll = contentEl.scrollHeight - contentEl.clientHeight;
        if (contentEl.scrollTop < maxScroll) {
          contentEl.scrollTop = Math.min(maxScroll, contentEl.scrollTop - frameDelta);
        }
      }
    };

    const onTouchEnd = () => {
      inIgnoredArea = false;
      nestedScrollable = null;
      contentEl = null;
      accumulatedPastTop = 0;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  // Auto-collapse after initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setSnapPoint(PEEK_SNAP);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);


  // Memoize children so they don't re-render during snap transitions
  const welcomeCard = useMemo(() => (
    <FloatingWelcomeCard authStatus={authStatus} profile={profile} minimized={false} />
  ), [authStatus, profile]);

  const searchResults = useMemo(() => <FloatingSearchResults fullWidth />, []);

  const communityJournal = useMemo(() => (
    <FloatingCommunityJournal
      minimized={false}
      onExpand={expandSheet}
    />
  ), [expandSheet]);

  const patientStories = useMemo(() => <FloatingPatientStories fullWidth />, []);

  const resources = useMemo(() => (
    <FloatingResources authStatus={authStatus} profile={profile} fullWidth />
  ), [authStatus, profile]);

  return (
    <Drawer.Root
      open
      onOpenChange={handleOpenChange}
      modal={false}
      disablePointerDismissal
      snapPoints={snapPoints}
      snapToSequentialPoints
      snapPoint={snapPoint}
      onSnapPointChange={handleSnapPointChange}
    >
      <Drawer.Portal>
        <Drawer.Viewport className={styles.Viewport}>
          <Drawer.Popup className={styles.Popup} initialFocus={false}>
            {/* Drag handle — click to toggle between peek and expanded */}
            <div className={styles.Handle} onClick={toggleSheet} />

            {/* Scrollable content — Drawer.Content handles scroll ↔ swipe handoff */}
            <Drawer.Content className={styles.Scroll} ref={scrollContainerRef} style={{ overflowY: isExpanded ? 'auto' : 'hidden' }}>
              <Stack direction="column" gap={2}>
                {welcomeCard}
                {searchResults}
                {communityJournal}
                {patientStories}
                {resources}
              </Stack>
            </Drawer.Content>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
